// First-party page-view beacon for fly-social.com.
//
// Mounted once inside the Router. On every public marketing-page load it sends
// a lightweight beacon to capture-site-view so the UTM Traffic cockpit can show
// where fly-social.com's OWN traffic comes from (Google, social, direct,
// campaigns) — the piece agency_embed_views never covered.
//
// Skips:
//   • the authed app (/fly-app) — it has its own analytics,
//   • agency embeds (/embed) — covered by agency_embed_views,
//   • white-label agency domains (__AGENCY_BRANDING__) — not fly-social.com.
//
// Deduped once per (path) per tab session so re-renders never double-count. The
// first beacon of a session is flagged is_entry — its referrer is the real
// external source (later in-session navigations carry an internal referrer).

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { captureUtmFromUrl } from '@/lib/utmTracking';

const FIRED_KEY = 'fly_site_views_fired_v1';
const SESSION_KEY = 'fly_site_session_v1';
const ENTRY_KEY = 'fly_site_entry_done_v1';

declare global {
  interface Window { __AGENCY_BRANDING__?: boolean }
}

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'no_session';
  }
}

function readFired(): Set<string> {
  try { return new Set(JSON.parse(sessionStorage.getItem(FIRED_KEY) || '[]') as string[]); }
  catch { return new Set(); }
}

export function SiteTrafficBeacon() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Only fly-social.com's own marketing pages.
    if (pathname.startsWith('/fly-app') || pathname.startsWith('/embed')) return;
    if (window.__AGENCY_BRANDING__) return;

    const isEntry = !sessionStorage.getItem(ENTRY_KEY);
    const utm = captureUtmFromUrl();

    // Dedupe per (path + campaign tags), NOT path alone. A path-only key meant a
    // UTM-tagged visit was skipped whenever the bare path had already been seen
    // this session (e.g. the visitor browsed fly-social.com first, then clicked
    // a tagged link to the same page) — so that campaign click never recorded.
    // Including the tags makes a tagged visit distinct, so it always lands.
    const utmSig = [utm.utm_source, utm.utm_medium, utm.utm_campaign].filter(Boolean).join('|');
    const dedupeKey = utmSig ? `${pathname}?${utmSig}` : pathname;

    const fired = readFired();
    if (fired.has(dedupeKey)) return;

    void supabase.functions.invoke('capture-site-view', {
      body: {
        path: pathname,
        referrer: document.referrer || null,
        is_entry: isEntry,
        utm_source: utm.utm_source ?? null,
        utm_medium: utm.utm_medium ?? null,
        utm_campaign: utm.utm_campaign ?? null,
        utm_term: utm.utm_term ?? null,
        utm_content: utm.utm_content ?? null,
        session_id: getSessionId(),
      },
    }).catch(() => { /* best-effort telemetry; never block the page */ });

    fired.add(dedupeKey);
    try { sessionStorage.setItem(FIRED_KEY, JSON.stringify([...fired])); } catch { /* quota */ }
    try { sessionStorage.setItem(ENTRY_KEY, '1'); } catch { /* quota */ }
  }, [pathname]);

  return null;
}
